import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Tooltip,
  TableSortLabel,
  Button,
  Typography,
} from '@mui/material';
import { EditOutlined, DeleteOutlined, Description, ViewList } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RetentionTable = ({ retentions, onEdit, onDelete, page, onPageChange, pagination, canEdit, canDelete, sortBy, sortDir, onSortChange }) => {
  const navigate = useNavigate();

  const SortCell = ({ label, field, align, sx: extraSx }) => (
    <TableCell align={align} onClick={() => onSortChange(field)} sx={{ cursor: 'pointer', userSelect: 'none', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (!retentions || retentions.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Description sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay tablas de retención registradas</p>
      </Paper>
    );
  }

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const totalPages = Math.ceil((pagination?.total || 0) / (pagination?.limit || 10));
  const from = pagination?.total ? ((page - 1) * (pagination?.limit || 10)) + 1 : 0;

  return (
    <Box>
      <Paper sx={{ overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortCell label="Nombre" field="name" sx={{ whiteSpace: 'nowrap' }} />
              <SortCell label="Código" field="code" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }} />
              <SortCell label="Empresa" field="company.name" sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }} />
              <SortCell label="Área" field="area.name" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }} />
              <SortCell label="Fecha" field="date" sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }} />
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>Líneas</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>Expedientes</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>Documentos</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {retentions.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((retention) => (
              <TableRow
                key={retention.id}
                hover
                onClick={() => navigate(`/retentions/${retention.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                    {retention.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2">{retention.code}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">{retention.company?.name || '-'}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2">{retention.area ? retention.area.name : '-'}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">{formatDate(retention.date)}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Typography variant="body2">{retention._count?.retentionLines || 0}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Typography variant="body2">{retention._count?.proceedings ?? 0}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Typography variant="body2">{retention._count?.documents ?? 0}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                    {canEdit && (
                      <Tooltip title="Editar">
                        <IconButton
                          sx={{ color: '#5d5d60', width: 36, height: 36 }}
                          onClick={(e) => { e.stopPropagation(); onEdit(retention); }}
                        >
                          <EditOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Eliminar">
                        <IconButton
                          sx={{ color: '#5d5d60', width: 36, height: 36 }}
                          onClick={(e) => { e.stopPropagation(); onDelete(retention.id); }}
                        >
                          <DeleteOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Paper>
      {pagination && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(29,31,32,0.55)' }}>
            Mostrando {from} de {pagination.total || 0} tablas
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" disabled={page <= 1} onClick={() => onPageChange(page - 1)} sx={{ fontWeight: 600, minWidth: 90 }}>Anterior</Button>
            <Button variant="outlined" size="small" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} sx={{ fontWeight: 600, minWidth: 90 }}>Siguiente</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RetentionTable;
